import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { useApplication } from './hooks/useApplication';

import { roles_names } from './data';

const RejectionAudit = ({ applicationData, mode, applicationId }) => {
  const { statuses, statusesLoading, statusesError, fetchApplicationStatuses } = useApplication(applicationId);
  const [sortConfig, setSortConfig] = React.useState({ key: 'timestamp', direction: 'desc' });

  useEffect(() => {
    if (applicationId) {
      fetchApplicationStatuses();
    }
  }, [applicationId, fetchApplicationStatuses]);

  const rejectionStatuses = statuses?.filter(status => 
    ['invite-declined', 'removed', 'banned'].includes(status.name)
  ) || [];

  const columns = [
    { key: 'fullName', label: 'ПІБ', sortable: true, width: '25%' },
    { key: 'role', label: 'Роль', sortable: true, width: '20%' },
    { key: 'email', label: 'Електронна пошта', sortable: true, width: '30%' },
    { key: 'timestamp', label: 'Дата та час', sortable: true, width: '25%' }
  ];

  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('uk-UA');
  };

  const getUserFullName = (action) => {
    if (!action || !action.detail) return 'Заявник';
    return action.detail.name || 'Користувач';
  };

  const getUserRole = (action) => {
    if (!action) return 'Заявник';
    
    const roleMap = {
      'system': 'Система',
      'user': 'Оператор',
      'applicant': 'Заявник'
    };

  
    if(action.detail && action.detail.role && roles_names[action.detail.role]) return roles_names[action.detail.role];
    
    return roleMap[action.source] || 'Заявник';
  };

  const getStatusText = (status) => {
    const statusMap = {
      'invite-declined': 'Відхилено заявку',
      'removed': 'Видалено',
      'banned': 'Заблоковано'
    };
    return statusMap[status] || status;
  };

  const sortedData = [...rejectionStatuses].sort((a, b) => {
    if (sortConfig.key === 'timestamp') {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      
      if (dateA < dateB) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (dateA > dateB) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    }
    
    if (sortConfig.key === 'fullName') {
      const nameA = getUserFullName(a.action);
      const nameB = getUserFullName(b.action);
      if (nameA < nameB) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (nameA > nameB) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    }
    
    if (sortConfig.key === 'role') {
      const roleA = getUserRole(a.action);
      const roleB = getUserRole(b.action);
      if (roleA < roleB) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (roleA > roleB) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    }
    
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const getSortIcon = (columnKey) => {
    if (columnKey !== sortConfig.key) return faSort;
    return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
  };

  if (statusesLoading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Завантаження...</span>
        </div>
        <p className="mt-2">Завантаження аудиту...</p>
      </div>
    );
  }

  if (statusesError) {
    return (
      <div className="alert alert-danger m-3">
        <strong>Помилка завантаження аудиту:</strong> {statusesError}
        <div className="mt-2">
          <button className="btn btn-sm btn-outline-danger" onClick={fetchApplicationStatuses}>
            Спробувати знову
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rejection-audit">
      <div className="audit-header">
        <h3 className="audit-title">Аудит відхилення заявки</h3>
        <p className="audit-description">
          Список користувачів, які брали участь у процесі відхилення заявки
        </p>
      </div>

      <div className="table-responsive">
        <table className="audit-table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th 
                  key={column.key}
                  style={{ width: column.width }}
                  onClick={() => column.sortable && handleSort(column.key)}
                  className={column.sortable ? 'sortable' : ''}
                >
                  <div className="d-flex align-items-center">
                    {column.label}
                    {column.sortable && (
                      <FontAwesomeIcon 
                        icon={getSortIcon(column.key)} 
                        className="ms-1" 
                        style={{ fontSize: '12px' }}
                      />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedData.map((item, index) => (
              <tr key={item._id || `audit-${index}`}>
                <td>
                  <div>{getUserFullName(item.action)}</div>
                </td>
                <td>
                  <div>{getUserRole(item.action)}</div>
                  <div><small className="text-muted">{getStatusText(item.name)}</small></div>
                  {item.action?.detail?.description && (
                    <div><small className="text-muted">Причина: {item.action.detail.description}</small></div>
                  )}
                  {item.kindergartenName && (
                    <div><small className="text-muted d-block">Заклад: {item.kindergartenName}</small></div>
                  )}
                </td>
                <td>{item.action?.detail?.email}</td>
                <td>{formatDateTime(item.date)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {sortedData.length === 0 && (
        <div className="no-data">
          Дані аудиту відсутні
        </div>
      )}
    </div>
  );
};

export default RejectionAudit;